import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicLayout } from "@/components/PublicLayout";
import { useI18n } from "@/i18n/I18nContext";
import { Seo } from "@/components/Seo";
import { GoogleReviewsSection } from "@/components/GoogleReviews";
import { Button } from "@/components/ui/button";
import { ArrowRight, CalendarDays, MapPin, Dumbbell, Sparkles, Zap, HeartPulse, CheckCircle2 } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1758875568097-ba62eeff66ad?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const FAQS = [
  {
    q: { es: "¿Cuál es el mejor gym en Coronado?", en: "What is the best gym in Coronado?", fr: "Quel est le meilleur gym à Coronado ?" },
    a: { es: "Frenchies Fitness & Pilates es el gimnasio premium de Coronado: fitness funcional, calistenia, fuerza y Pilates Reformer, con coaching de alto nivel y clases privadas.",
         en: "Frenchies Fitness & Pilates is Coronado's premium gym: functional fitness, calisthenics, strength and Pilates Reformer, with top-level coaching and private classes.",
         fr: "Frenchies Fitness & Pilates est le gym premium de Coronado : fitness fonctionnel, calisthénie, force et Pilates Reformer, avec un coaching de haut niveau et des cours privés." },
  },
  {
    q: { es: "¿Dónde hacer sport y fitness en Coronado?", en: "Where to do sport and fitness in Coronado?", fr: "Où faire du sport et du fitness à Coronado ?" },
    a: { es: "En Frenchies, en el corazón de Coronado (Panamá Oeste). Ofrecemos entrenamientos para todos los niveles, desde principiantes hasta atletas.",
         en: "At Frenchies, in the heart of Coronado (Panamá Oeste). We offer training for every level, from beginners to athletes.",
         fr: "Chez Frenchies, au cœur de Coronado (Panamá Oeste). Nous proposons des entraînements pour tous les niveaux, du débutant à l'athlète." },
  },
  {
    q: { es: "¿Cómo reservar una clase en el gym de Coronado?", en: "How to book a class at the Coronado gym?", fr: "Comment réserver un cours au gym de Coronado ?" },
    a: { es: "Reserva en línea en segundos desde nuestro horario y paga de forma segura. Elige tu clase de fitness o Pilates y asegura tu lugar.",
         en: "Book online in seconds from our schedule and pay securely. Pick your fitness or Pilates class and secure your spot.",
         fr: "Réserve en ligne en quelques secondes depuis notre planning et paie en toute sécurité. Choisis ton cours de fitness ou Pilates et garde ta place." },
  },
];

export default function GymCoronado() {
  const { tl } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "gym-coronado-ldjson";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q.es,
        "acceptedAnswer": { "@type": "Answer", "text": f.a.es },
      })),
    });
    document.head.appendChild(script);
    return () => { document.getElementById("gym-coronado-ldjson")?.remove(); };
  }, []);

  const PERKS = [
    { icon: Dumbbell, t: { es: "Fitness funcional & fuerza", en: "Functional fitness & strength", fr: "Fitness fonctionnel & force" } },
    { icon: Zap, t: { es: "Functional movement & calistenia", en: "Functional movement & calisthenics", fr: "Functional movement & calisthénie" } },
    { icon: Sparkles, t: { es: "Pilates Reformer", en: "Pilates Reformer", fr: "Pilates Reformer" } },
    { icon: HeartPulse, t: { es: "Coaching premium & clases privadas", en: "Premium coaching & private classes", fr: "Coaching premium & cours privés" } },
  ];

  return (
    <PublicLayout>
      <Seo
        title="Gym en Coronado, Panamá — Frenchies Fitness & Pilates | Gym, Functional Movement & Fitness"
        description="Le gym de référence à Coronado, Panamá pour le sport et le fitness : fitness fonctionnel, calisthénie et Pilates Reformer. Réserve ta séance en ligne."
        path="/gym-coronado"
        keywords="Gym, Gym Coronado, Gym Panamá, Functional Movement Coronado, Sport Coronado, Fitness Coronado, gimnasio Coronado, deporte Coronado, Pilates Coronado, gym Panamá Oeste"
      />

      {/* HERO */}
      <section className="relative flex min-h-[70vh] w-full items-center overflow-hidden bg-black">
        <img src={HERO_IMG} alt="Gym à Coronado — Frenchies Fitness" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
        <div className="relative z-10 mx-auto w-full max-w-4xl px-5 py-20 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-gold">
            <MapPin className="h-4 w-4" /> Coronado · Panamá Oeste
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {tl({ es: "Tu gym en Coronado", en: "Your gym in Coronado", fr: "Ton gym à Coronado" })}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.22 }}
            className="mt-5 max-w-xl text-base text-white/85 md:text-lg">
            {tl({ es: "Sport, fitness y Pilates de alto nivel en el corazón de Coronado. Entrena con los mejores coaches y reserva tu clase en línea.",
                  en: "High-level sport, fitness and Pilates in the heart of Coronado. Train with the best coaches and book your class online.",
                  fr: "Sport, fitness et Pilates de haut niveau au cœur de Coronado. Entraîne-toi avec les meilleurs coachs et réserve ton cours en ligne." })}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.34 }}
            className="mt-8 flex flex-wrap items-center gap-3">
            <motion.button data-testid="gym-coronado-book-btn" onClick={() => navigate("/schedule")}
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-[0_0_34px_hsl(var(--primary)/0.5)]">
              <CalendarDays className="h-5 w-5" />
              {tl({ es: "Reservar mi clase", en: "Book my session", fr: "Réserver ma séance" })}
              <ArrowRight className="h-4 w-4" />
            </motion.button>
            <Button size="lg" variant="outline" data-testid="gym-coronado-pricing-btn" onClick={() => navigate("/pricing")}
              className="rounded-full border-white/40 bg-white/10 text-white hover:bg-white/20">
              {tl({ es: "Ver precios", en: "See pricing", fr: "Voir les tarifs" })}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
          {tl({ es: "El gym, sport y fitness de referencia en Coronado", en: "The go-to gym, sport & fitness in Coronado", fr: "Le gym, sport et fitness de référence à Coronado" })}
        </h2>
        <p className="mt-4 leading-relaxed text-muted-foreground">
          {tl({
            es: "Frenchies Fitness & Pilates es el gimnasio (gym) premium de Coronado, Panamá. Si buscas un gym en Coronado, un centro de sport en Coronado o clases de fitness en Coronado, aquí encuentras entrenamientos para todos los niveles: fitness funcional, calistenia, fuerza y Pilates Reformer. Nuestro equipo de coaches te acompaña con un enfoque premium, seguro y motivador, en pleno Coronado (Panamá Oeste).",
            en: "Frenchies Fitness & Pilates is the premium gym in Coronado, Panama. If you're looking for a gym in Coronado, a sport center in Coronado or fitness classes in Coronado, you'll find training for every level: functional fitness, calisthenics, strength and Pilates Reformer. Our coaching team supports you with a premium, safe and motivating approach, right in Coronado (Panamá Oeste).",
            fr: "Frenchies Fitness & Pilates est le gym premium de Coronado, Panama. Si tu cherches un gym à Coronado, un centre de sport à Coronado ou des cours de fitness à Coronado, tu trouveras des entraînements pour tous les niveaux : fitness fonctionnel, calisthénie, force et Pilates Reformer. Notre équipe de coachs t'accompagne avec une approche premium, sûre et motivante, en plein cœur de Coronado (Panamá Oeste)." })}
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {PERKS.map(({ icon: Icon, t }, i) => (
            <div key={i} data-testid={`gym-perk-${i}`} className="flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4">
              <Icon className="h-6 w-6 shrink-0 text-primary" />
              <span className="font-medium">{tl(t)}</span>
            </div>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-secondary/40 py-16">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <h2 className="mb-6 font-display text-2xl font-bold tracking-tight md:text-3xl">
            {tl({ es: "¿Por qué elegir Frenchies en Coronado?", en: "Why choose Frenchies in Coronado?", fr: "Pourquoi choisir Frenchies à Coronado ?" })}
          </h2>
          <ul className="space-y-3">
            {[
              { es: "Coaches certificados y clases en grupos reducidos", en: "Certified coaches and small-group classes", fr: "Coachs certifiés et cours en petits groupes" },
              { es: "Reserva y pago 100% en línea, sin complicaciones", en: "100% online booking and payment, hassle-free", fr: "Réservation et paiement 100% en ligne, sans prise de tête" },
              { es: "Fitness, calistenia y Pilates Reformer bajo un mismo techo", en: "Fitness, calisthenics and Pilates Reformer under one roof", fr: "Fitness, calisthénie et Pilates Reformer sous un même toit" },
              { es: "Ubicación céntrica en Coronado, fácil acceso", en: "Central location in Coronado, easy access", fr: "Emplacement central à Coronado, accès facile" },
            ].map((item, i) => (
              <li key={i} data-testid={`gym-why-${i}`} className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span className="text-muted-foreground">{tl(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <h2 className="mb-6 font-display text-2xl font-bold tracking-tight md:text-3xl">
          {tl({ es: "Preguntas frecuentes", en: "Frequently asked questions", fr: "Questions fréquentes" })}
        </h2>
        <div className="space-y-5">
          {FAQS.map((f, i) => (
            <div key={i} data-testid={`gym-faq-${i}`} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-semibold">{tl(f.q)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{tl(f.a)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <GoogleReviewsSection compact />

      <section className="mx-auto max-w-5xl px-5 py-16 text-center lg:px-8">
        <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
          {tl({ es: "Empieza hoy en tu gym de Coronado", en: "Start today at your Coronado gym", fr: "Commence dès aujourd'hui dans ton gym de Coronado" })}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
          {tl({ es: "Reserva tu primera clase de sport y fitness en Coronado en menos de un minuto.", en: "Book your first sport and fitness class in Coronado in under a minute.", fr: "Réserve ton premier cours de sport et fitness à Coronado en moins d'une minute." })}
        </p>
        <Button size="lg" data-testid="gym-coronado-cta-btn" onClick={() => navigate("/schedule")} className="mt-7 rounded-full">
          {tl({ es: "Reservar mi clase", en: "Book my session", fr: "Réserver ma séance" })} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </section>
    </PublicLayout>
  );
}
